// Наблюдатель ленты основной точки — монтируется ОДИН раз, в `AppLayout` (T-047).
//
// Смонтирован ровно тогда, когда жива сессия: на экране карточек без входа (`AppLayout`
// там не смонтирован) опроса и уведомлений нет (решение владельца 4). Саппорт-сессия
// ленту не опрашивает: устройство платформы показывало бы уведомления чужой точки.

import { useNavigate } from '@tanstack/react-router';
import { useEffect, useSyncExternalStore } from 'react';

import { t } from '@/shared/i18n';
import { showSystemNotification, systemNotificationSupport } from '@/shared/lib/systemNotification';

import { useAlertFeed, useFeedLocation } from './api/useAlertFeed';
import {
  deviceMarksVersion,
  markNotified,
  markSeen,
  notifiedMark,
  notifyEnabled,
  seenMark,
  subscribeDeviceMarks,
} from './model/deviceMarks';
import { notifyPlan } from './model/notifyPlan';

export function useAlertFeedWatcher(): void {
  const location = useFeedLocation();
  const feed = useAlertFeed(location, { poll: true });
  const navigate = useNavigate();
  const marks = useSyncExternalStore(subscribeDeviceMarks, deviceMarksVersion, deviceMarksVersion);
  const data = feed.data;

  useEffect(() => {
    if (location === null || !data?.available) return;
    // Первый запуск на устройстве: история видна, но «новой» не считается — иначе новый
    // планшет встречал бы «99+».
    if (seenMark(location) === null) {
      markSeen(location, data.messages[0]?.created_at ?? data.serverTime);
    }
    const plan = notifyPlan({
      allowed: notifyEnabled() && systemNotificationSupport() === 'ok',
      location,
      messages: data.messages,
      mark: notifiedMark(location),
      serverTime: data.serverTime,
    });
    // Отметка — ДО показа: второй эффект StrictMode видит её сдвинутой и не дублирует тост.
    if (plan.mark !== null) markNotified(location, plan.mark);
    if (plan.show !== null) {
      showSystemNotification({
        title: plan.show.title,
        body:
          plan.count > 1
            ? t('alerts.summary.many').replace('{count}', String(plan.count))
            : plan.show.body,
        tag: plan.show.tag,
        onClick: () => void navigate({ to: '/kiosk/alerts' }),
      });
    }
  }, [location, data, marks, navigate]);
}
